import React, { useState, useEffect } from 'react';
import { CaraLoomLogo } from '../components/brand/CaraLoomLogo';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Activity, Users, Shield, AlertTriangle, CheckCircle,
  Calendar, FileText, BarChart3, LogOut, Moon, Sun,
  Bell, Settings, TrendingUp, UserCheck, Clock, XCircle,
  DollarSign, UserCog, ClipboardList, Receipt, IdCard, Loader2, ShieldAlert, ShieldCheck, Inbox, Building2, Lock, Landmark, Radio, Crown, Video
} from 'lucide-react';
import { toast } from 'sonner';
import { AdminUsersPanel } from '../components/admin/AdminUsersPanel';
import { AdminRevenuePanel } from '../components/admin/AdminRevenuePanel';
import { AdminReferralsPanel } from '../components/admin/AdminReferralsPanel';
import { AdminClaimsPanel } from '../components/admin/AdminClaimsPanel';
import { AdminTrustSafetyPanel } from '../components/admin/AdminTrustSafetyPanel';
import { SafetyShieldPanel } from '../components/admin/SafetyShieldPanel';
import { LeadsPanel } from '../components/admin/LeadsPanel';
import { InstitutionInquiriesPanel } from '../components/admin/InstitutionInquiriesPanel';
import { AttributionPanel } from '../components/admin/AttributionPanel';
import SecurityPanel from '../components/admin/SecurityPanel';
import { AdminPricingPanel } from '../components/admin/AdminPricingPanel';
import { AdminCareLibraryPanel } from '../components/admin/AdminCareLibraryPanel';
import { DischargeActivationTile } from '../components/admin/DischargeActivationTile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout, getAuthHeader, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [clinicians, setClinicians] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('clinicians');
  const [npiChecking, setNpiChecking] = useState(null);
  const [safetyOpen, setSafetyOpen] = useState(0);
  const [newLeads, setNewLeads] = useState(0);
  const [newInquiries, setNewInquiries] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, cliniciansRes, patientsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, getAuthHeader()),
        axios.get(`${API}/admin/clinicians`, getAuthHeader()),
        axios.get(`${API}/admin/patients`, getAuthHeader())
      ]);
      setStats(statsRes.data);
      setClinicians(cliniciansRes.data);
      setPatients(patientsRes.data);
      try {
        const safetyRes = await axios.get(`${API}/safety/summary`, getAuthHeader());
        setSafetyOpen((safetyRes.data.open || 0) + (safetyRes.data.reviewing || 0));
      } catch (e) { /* non-blocking */ }
      try {
        const leadsRes = await axios.get(`${API}/admin/leads?status=new`, getAuthHeader());
        setNewLeads(leadsRes.data.summary?.new || 0);
      } catch (e) { /* non-blocking */ }
      try {
        const instRes = await axios.get(`${API}/admin/institution-inquiries?status=new`, getAuthHeader());
        setNewInquiries(instRes.data.summary?.new || 0);
      } catch (e) { /* non-blocking */ }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyClinician = async (clinicianId) => {
    try {
      await axios.put(`${API}/admin/clinicians/${clinicianId}/verify`, {}, getAuthHeader());
      toast.success('Clinician verified!');
      fetchData();
    } catch (error) {
      toast.error('Failed to verify clinician');
    }
  };

  const runNpiCheck = async (clinicianId) => {
    setNpiChecking(clinicianId);
    try {
      const { data } = await axios.post(`${API}/admin/clinicians/${clinicianId}/verify-npi`, {}, getAuthHeader());
      if (data.verified) toast.success(`NPI verified via NPPES — ${data.registry_name}`);
      else toast.warning(data.message || 'NPI not verified');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not run NPI check');
    } finally {
      setNpiChecking(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Iter-119 reconciliation — the "Pending verifications" concept must
  // match /api/admin/stats.pending_verifications exactly (that query counts
  // clinician_profiles with license_verified=False). Client-side filtering
  // over the full clinicians array double-counted profile-incomplete rows
  // (clinicians without any profile yet), producing a wildly different
  // number than the KPI tile. We filter using the exact same predicate the
  // backend uses so the tab badge, the sidebar badge, and the KPI tile all
  // agree.
  const pendingVerifications = clinicians.filter(
    (c) => c.profile && c.profile.license_verified === false,
  );
  const highRiskPatients = patients.filter(p => p.profile?.urgency_level === 'High Risk');

  if (loading) {
    return (
      <div className="min-h-screen bg-cn-bg dark:bg-cn-ink-900 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-cn-sage-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cn-bg dark:bg-cn-ink-900" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white dark:bg-cn-ink-800 border-r border-slate-200 dark:border-cn-ink-700 p-4 hidden lg:block">
        <div className="flex items-center gap-3 mb-8">
          <CaraLoomLogo size={30} showWordmark={false} />
          <div>
            <span className="text-lg font-bold text-slate-900 dark:text-white font-display">CaraLoom</span>
            <p className="text-xs text-cn-primary dark:text-cn-sage-300 font-medium">Admin Portal</p>
          </div>
        </div>

        <nav className="space-y-1">
          {[
            { icon: UserCheck, label: 'Clinicians', tab: 'clinicians' },
            { icon: Users, label: 'Patients', tab: 'patients' },
            { icon: ShieldAlert, label: 'Trust & Safety', tab: 'trust-safety' },
            { icon: ShieldCheck, label: 'Safety Shield', tab: 'safety-shield' },
            { icon: Inbox, label: 'Leads', tab: 'leads' },
            { icon: Building2, label: 'Institutions', tab: 'institutions' },
            { icon: TrendingUp, label: 'Attribution', tab: 'attribution' },
            { icon: Shield, label: 'Verifications', tab: 'verifications' },
            { icon: AlertTriangle, label: 'Risk Alerts', tab: 'alerts' },
            { icon: UserCog, label: 'Users', tab: 'users' },
            { icon: DollarSign, label: 'Revenue', tab: 'revenue' },
            { icon: Receipt, label: 'Claims (RCM)', tab: 'claims' },
            { icon: Crown, label: 'Pricing & Tiers', tab: 'pricing' },
            { icon: Video, label: 'Care Library', tab: 'care-library' },
          ].map((item, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(item.tab)}
              data-testid={`admin-nav-${item.tab}`}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors w-full text-left ${
                activeTab === item.tab
                  ? 'bg-cn-sage-50 dark:bg-cn-sage-900/30 text-cn-primary dark:text-cn-sage-200'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-cn-ink-700/50'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </button>
          ))}
          <button
            onClick={() => navigate('/admin/payments')}
            data-testid="admin-nav-payments-ops"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors w-full text-left text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-cn-ink-700/50"
          >
            <Landmark className="w-5 h-5" />
            Payments Ops
          </button>
          <button
            onClick={() => navigate('/admin/observability')}
            data-testid="admin-nav-observability"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors w-full text-left text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-cn-ink-700/50"
          >
            <Radio className="w-5 h-5" />
            Live Observability
          </button>
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={handleLogout}
            data-testid="admin-logout-btn"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-cn-ink-700/50 w-full transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white font-display">
              Admin Dashboard
            </h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              Platform overview and management
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-cn-ink-700 transition-colors"
            >
              {theme === 'light' ? <Moon className="w-5 h-5 text-slate-600" /> : <Sun className="w-5 h-5 text-yellow-400" />}
            </button>
            <button className="p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-cn-ink-700 transition-colors relative">
              <Bell className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              {(pendingVerifications.length + highRiskPatients.length) > 0 && (
                <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                  {pendingVerifications.length + highRiskPatients.length}
                </span>
              )}
            </button>
          </div>
        </header>

        {/* Alert Banners */}
        {highRiskPatients.length > 0 && (
          <Card className="mb-6 border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{highRiskPatients.length} High Risk Patient(s)</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Requires immediate attention</p>
                </div>
              </div>
              <a href="#alerts">
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6">
                  Review Now
                </Button>
              </a>
            </CardContent>
          </Card>
        )}

        {/* Discharge-pilot activation metric (Feb 2026). Sits above the standard
            stats grid because (a) it crosses tabs (referrals + clinicians +
            claims) and (b) it's THE single number to see at login if a
            hospital pilot is running. */}
        <DischargeActivationTile />

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total Patients', value: stats?.total_patients || 0, icon: Users, color: 'teal' },
            { label: 'Total Clinicians', value: stats?.total_clinicians || 0, icon: UserCheck, color: 'blue' },
            { label: 'Active Matches', value: stats?.total_matches || 0, icon: TrendingUp, color: 'green' },
            { label: 'Appointments', value: stats?.total_appointments || 0, icon: Calendar, color: 'purple' },
            { label: 'Pending Verifications', value: stats?.pending_verifications || 0, icon: Clock, color: 'orange' }
          ].map((stat, index) => (
            <Card key={index} className="bg-white dark:bg-cn-ink-800 border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{stat.label}</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white font-display mt-1">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${stat.color}-100 dark:bg-${stat.color}-900/30`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white dark:bg-cn-ink-800 p-1 rounded-xl">
            <TabsTrigger value="clinicians" className="rounded-lg" data-testid="admin-clinicians-tab">
              <UserCheck className="w-4 h-4 mr-2" />
              Clinicians
            </TabsTrigger>
            <TabsTrigger value="patients" className="rounded-lg" data-testid="admin-patients-tab">
              <Users className="w-4 h-4 mr-2" />
              Patients
            </TabsTrigger>
            <TabsTrigger value="trust-safety" className="rounded-lg" data-testid="admin-trust-safety-tab">
              <ShieldAlert className="w-4 h-4 mr-2" />
              Trust &amp; Safety
            </TabsTrigger>
            <TabsTrigger value="safety-shield" className="rounded-lg" data-testid="admin-safety-shield-tab">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Safety Shield
              {(safetyOpen ?? 0) > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">{safetyOpen}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="leads" className="rounded-lg" data-testid="admin-leads-tab">
              <Inbox className="w-4 h-4 mr-2" />
              Leads
              {(newLeads ?? 0) > 0 && (
                <Badge className="ml-2 bg-cn-accent text-white">{newLeads}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="institutions" className="rounded-lg" data-testid="admin-institutions-tab">
              <Building2 className="w-4 h-4 mr-2" />
              Institutions
              {(newInquiries ?? 0) > 0 && (
                <Badge className="ml-2 bg-cn-accent text-white">{newInquiries}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="attribution" className="rounded-lg" data-testid="admin-attribution-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Attribution
            </TabsTrigger>
            <TabsTrigger value="verifications" className="rounded-lg" data-testid="admin-verifications-tab">
              <Shield className="w-4 h-4 mr-2" />
              Verifications
              {pendingVerifications.length > 0 && (
                <Badge className="ml-2 bg-orange-500 text-white">{pendingVerifications.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="alerts" className="rounded-lg" data-testid="admin-alerts-tab">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Risk Alerts
              {highRiskPatients.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">{highRiskPatients.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg" data-testid="admin-users-tab">
              <UserCog className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="revenue" className="rounded-lg" data-testid="admin-revenue-tab">
              <DollarSign className="w-4 h-4 mr-2" />
              Revenue
            </TabsTrigger>
            <TabsTrigger value="referrals" className="rounded-lg" data-testid="admin-referrals-tab">
              <ClipboardList className="w-4 h-4 mr-2" />
              Referrals
            </TabsTrigger>
            <TabsTrigger value="claims" className="rounded-lg" data-testid="admin-claims-tab">
              <Receipt className="w-4 h-4 mr-2" />
              Claims (RCM)
            </TabsTrigger>
            <TabsTrigger value="security" className="rounded-lg" data-testid="admin-security-tab">
              <Lock className="w-4 h-4 mr-2" />
              Security
            </TabsTrigger>
            <TabsTrigger value="pricing" className="rounded-lg" data-testid="admin-pricing-tab">
              <Crown className="w-4 h-4 mr-2" />
              Pricing &amp; Tiers
            </TabsTrigger>
            <TabsTrigger value="care-library" className="rounded-lg" data-testid="admin-care-library-tab">
              <Video className="w-4 h-4 mr-2" />
              Care Library
            </TabsTrigger>
          </TabsList>

          {/* Clinicians Tab */}
          <TabsContent value="clinicians" id="clinicians">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display">All Clinicians</CardTitle>
                <CardDescription>{clinicians.length} registered clinicians</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {clinicians.map((clinician, index) => (
                    <div key={clinician.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl" data-testid={`admin-clinician-${index}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-cn-sage-100 dark:bg-cn-sage-900/30 rounded-xl flex items-center justify-center">
                          <span className="font-bold text-cn-primary dark:text-cn-primary">
                            {clinician.first_name?.[0]}{clinician.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-white">
                            {clinician.first_name} {clinician.last_name}
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {clinician.profile?.license_type || 'N/A'} • {clinician.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={clinician.profile?.license_verified ? 'badge-success' : 'badge-warning'}>
                          {clinician.profile?.license_verified ? 'Verified' : 'Pending'}
                        </Badge>
                        <Badge className={
                          clinician.profile?.background_check_status === 'passed' ? 'badge-success' :
                          clinician.profile?.background_check_status === 'failed' ? 'badge-danger' : 'badge-warning'
                        }>
                          BG: {clinician.profile?.background_check_status || 'Pending'}
                        </Badge>
                        <Badge
                          className={clinician.profile?.npi_verified ? 'badge-success' : 'badge-warning'}
                          data-testid={`npi-badge-${index}`}
                        >
                          NPI: {clinician.profile?.npi_verified ? 'Verified' : (clinician.profile?.npi ? 'Unverified' : 'None')}
                        </Badge>
                        {clinician.profile?.npi && (
                          <Button
                            onClick={() => runNpiCheck(clinician.id)}
                            disabled={npiChecking === clinician.id}
                            variant="outline"
                            className="rounded-full h-8 px-3 text-xs"
                            data-testid={`run-npi-check-${index}`}
                          >
                            {npiChecking === clinician.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><IdCard className="w-3.5 h-3.5 mr-1" /> NPI check</>}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Patients Tab */}
          <TabsContent value="patients" id="patients">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display">All Patients</CardTitle>
                <CardDescription>{patients.length} registered patients</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {patients.map((patient, index) => (
                    <div key={patient.id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl" data-testid={`admin-patient-${index}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
                          <span className="font-bold text-red-600 dark:text-red-400">
                            {patient.first_name?.[0]}{patient.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-white">
                            {patient.first_name} {patient.last_name}
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{patient.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {patient.profile?.care_complexity_score && (
                          <Badge variant="outline">
                            Complexity: {patient.profile.care_complexity_score}/100
                          </Badge>
                        )}
                        <Badge className={
                          patient.profile?.urgency_level === 'High Risk' ? 'badge-danger' :
                          patient.profile?.urgency_level === 'Moderate' ? 'badge-warning' : 'badge-success'
                        }>
                          {patient.profile?.urgency_level || 'N/A'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Trust & Safety Tab */}
          <TabsContent value="trust-safety" id="trust-safety">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display">Trust &amp; Safety inbox</CardTitle>
                <CardDescription>At-risk signals across the platform — unverified NPIs, license issues, referral SLA breaches and thin profiles — prioritized into one actionable list</CardDescription>
              </CardHeader>
              <CardContent>
                <AdminTrustSafetyPanel onGoToReferrals={() => setActiveTab('referrals')} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Safety Shield Tab */}
          <TabsContent value="safety-shield" id="safety-shield">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-cn-primary" /> Safety Shield — Supervising-RN review queue
                </CardTitle>
                <CardDescription>Automated clinical governance. The AI audit engine scans active patients&apos; vitals for missed monitoring and clinical anomalies; every event is immutably logged for the audit trail (malpractice-premium evidence).</CardDescription>
              </CardHeader>
              <CardContent>
                <SafetyShieldPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leads Tab */}
          <TabsContent value="leads" id="leads">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Inbox className="w-5 h-5 text-cn-primary" /> Care leads — local SEO capture
                </CardTitle>
                <CardDescription>Families who requested a match from the hyper-local city pages. Follow up fast — new leads convert best within the hour.</CardDescription>
              </CardHeader>
              <CardContent>
                <LeadsPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Institutions Tab */}
          <TabsContent value="institutions" id="institutions">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-cn-primary" /> Institutional inquiries — enterprise pipeline
                </CardTitle>
                <CardDescription>Hospitals, health systems &amp; payers requesting a demo or Discharge-API access. The highest-value B2B conversion path — respond fast.</CardDescription>
              </CardHeader>
              <CardContent>
                <InstitutionInquiriesPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Attribution Tab */}
          <TabsContent value="attribution" id="attribution">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cn-primary" /> Attribution — one funnel, every channel
                </CardTitle>
                <CardDescription>Family leads, clinician share kits, local-SEO, and B2B inquiries rolled into a single conversion view (by <code>?ref=</code>) — plus issued Discharge-API sandbox keys.</CardDescription>
              </CardHeader>
              <CardContent>
                <AttributionPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verifications Tab */}
          <TabsContent value="verifications" id="verifications">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display">Pending Verifications</CardTitle>
                <CardDescription>{pendingVerifications.length} clinicians awaiting verification</CardDescription>
              </CardHeader>
              <CardContent>
                {pendingVerifications.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">All Caught Up!</h3>
                    <p className="text-slate-500 dark:text-slate-400">No pending verifications</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingVerifications.map((clinician, index) => (
                      <div key={clinician.id} className="flex items-center justify-between p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-900/50" data-testid={`pending-verification-${index}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                            <Shield className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900 dark:text-white">
                              {clinician.first_name} {clinician.last_name}
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {clinician.profile?.license_type} • License: {clinician.profile?.license_number || 'N/A'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => verifyClinician(clinician.id)}
                            data-testid={`verify-clinician-${index}`}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-full px-4"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            className="rounded-full px-4 border-red-200 text-red-600 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Risk Alerts Tab */}
          <TabsContent value="alerts" id="alerts">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display">High Risk Patients</CardTitle>
                <CardDescription>{highRiskPatients.length} patients requiring attention</CardDescription>
              </CardHeader>
              <CardContent>
                {highRiskPatients.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No Critical Alerts</h3>
                    <p className="text-slate-500 dark:text-slate-400">All patients are within normal risk levels</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {highRiskPatients.map((patient, index) => (
                      <div key={patient.id} className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-900/50" data-testid={`high-risk-patient-${index}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
                            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900 dark:text-white">
                              {patient.first_name} {patient.last_name}
                            </h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              Complexity: {patient.profile?.care_complexity_score}/100 • {patient.profile?.recommended_care_level}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className="badge-danger">High Risk</Badge>
                          <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-4">
                            Review Case
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" id="users">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display">User management</CardTitle>
                <CardDescription>Search, verify, suspend or remove platform members</CardDescription>
              </CardHeader>
              <CardContent>
                <AdminUsersPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue" id="revenue">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display">Revenue cycle</CardTitle>
                <CardDescription>Settlement ledger, platform fees and clinician payouts</CardDescription>
              </CardHeader>
              <CardContent>
                <AdminRevenuePanel />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="referrals" id="referrals">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display">Discharge-planner referrals</CardTitle>
                <CardDescription>Concierge intake from hospitals & case managers — track placement status</CardDescription>
              </CardHeader>
              <CardContent>
                <AdminReferralsPanel />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="claims" id="claims">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display">Revenue cycle — claims oversight</CardTitle>
                <CardDescription>Platform-wide billing: charges captured, collections, outstanding A/R and platform fees</CardDescription>
              </CardHeader>
              <CardContent>
                <AdminClaimsPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security tab — MFA coverage, rate-limit/lockout activity, config health */}
          <TabsContent value="security" id="security">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Lock className="w-5 h-5 text-cn-primary" /> Security &amp; trust posture
                </CardTitle>
                <CardDescription>
                  The institutional security surface — multi-factor authentication coverage,
                  abuse-prevention activity (rate-limits + login lockouts), webhook delivery
                  health, and live configuration sanity checks. The view a procurement team
                  asks for during a SOC-2 vendor review.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SecurityPanel token={token} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing & Tiers tab — Stage 2 auto-trigger progress, reservation
              waitlist, manual user-tier setter. Used to operate the hybrid
              SaaS+marketplace pricing model before live Stripe billing is on. */}
          <TabsContent value="pricing" id="pricing">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Crown className="w-5 h-5 text-cn-primary" /> Pricing &amp; tiers
                </CardTitle>
                <CardDescription>
                  Where the marketplace economics are controlled — the Stage 2 auto-trigger that flips on paid subscription billing once the pre-launch waitlist crosses the configured threshold, the full reservation waitlist, and the manual tier override used during early Pro enrollment.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminPricingPanel getAuthHeader={getAuthHeader} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Care Library tab — manage the caregiver + clinician video catalog.
              Seed rows (17 in backend/routes/care_library.py) are read-only;
              custom rows (with matching id) override seeds on the public read. */}
          <TabsContent value="care-library" id="care-library">
            <Card className="bg-white dark:bg-cn-ink-800 border-0">
              <CardHeader>
                <CardTitle className="font-display flex items-center gap-2">
                  <Video className="w-5 h-5 text-cn-primary" /> Care Library
                </CardTitle>
                <CardDescription>
                  Curated caregiver &amp; clinician video catalog. Seed rows are protected — to override a seed entry, add a custom row with the same id. All entries must follow §5 of the curation prompt (license, attribution, lane, image caveat, clinical review).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AdminCareLibraryPanel getAuthHeader={getAuthHeader} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
